import { Box, Button, TextField } from "@mui/material";
import dayjs from "dayjs";
import AsyncSelect, { AsyncSelectValue } from "@/components/AsyncSelect";
import CustomDatePicker from "@/components/CustomDatePicker";
import CustomSelect from "@/components/CustomSelect";
import { inputLabel } from "@/components/MaskInput";
import NumberInput from "@/components/NumberInput";
import {
  DOSAGE_LIST,
  FREQUENCY_LIST,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/medication/medication-constant";
import EntryCard from "./EntryCard";
import { useSectionRequirement } from "../section-requirement-context";
import { FREE_TEXT_MAX_ROWS } from "../readmission-f2f-constant";
import {
  MEDICATION_LABELS,
  MEDICATION_PLACEHOLDERS,
} from "../readmission-f2f-medication-constant";
import { MedicationEntry } from "../readmission-f2f-types";

const toText = (value: unknown): string =>
  typeof value === "string" ? value : "";

const FULL_WIDTH_SX = { gridColumn: "1 / -1" };

export interface MedicationEntryCardProps {
  entry: MedicationEntry;
  medicineOptions: AsyncSelectValue[];
  isMedicineLoading: boolean;
  onMedicineSearch: (value: string) => void;
  onChange: (patch: Partial<MedicationEntry>) => void;
  actionLabel: string;
  isActionDisabled: boolean;
  onAction: () => void;
  onRemove?: () => void;
  removeLabel?: string;
  /** The new-entry row: its fields are validated by the add action, not by Save & Sign. */
  isDraft?: boolean;
}

const MedicationEntryCard = ({
  entry,
  medicineOptions,
  isMedicineLoading,
  onMedicineSearch,
  onChange,
  actionLabel,
  isActionDisabled,
  onAction,
  onRemove,
  removeLabel,
  isDraft = false,
}: MedicationEntryCardProps) => {
  const { missingError } = useSectionRequirement();
  const savedError = (value: unknown) =>
    isDraft ? undefined : missingError(value);
  return (
    <EntryCard onRemove={onRemove} removeLabel={removeLabel}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(130px, 1fr))"
        gap={1.5}
      >
        <Box sx={FULL_WIDTH_SX}>
          <AsyncSelect
            required
            error={savedError(entry.medicineName)}
            label={MEDICATION_LABELS.name}
            placeholder={MEDICATION_PLACEHOLDERS.name}
            loading={isMedicineLoading}
            options={medicineOptions}
            value={
              entry.medicineId
                ? { value: entry.medicineId, label: entry.medicineName }
                : null
            }
            onSearch={onMedicineSearch}
            onChange={(selected) =>
              onChange({
                medicineId: selected?.value ?? "",
                medicineName: selected?.label ?? "",
              })
            }
          />
        </Box>
        <NumberInput
          label={MEDICATION_LABELS.quantity}
          textFieldProps={{
            placeholder: MEDICATION_PLACEHOLDERS.quantity,
            value: entry.quantity,
            onChange: (e) => onChange({ quantity: e.target.value }),
          }}
        />
        <CustomSelect
          label={MEDICATION_LABELS.dosage}
          placeholder={MEDICATION_PLACEHOLDERS.dosage}
          options={DOSAGE_LIST}
          value={entry.dosage}
          onChange={(value) => onChange({ dosage: toText(value) })}
        />
        <CustomSelect
          label={MEDICATION_LABELS.frequency}
          placeholder={MEDICATION_PLACEHOLDERS.frequency}
          options={FREQUENCY_LIST}
          value={entry.frequency}
          onChange={(value) => onChange({ frequency: toText(value) })}
        />
        <CustomDatePicker
          required
          error={savedError(entry.startDate)}
          label={MEDICATION_LABELS.startDate}
          placeholder={MEDICATION_PLACEHOLDERS.date}
          value={entry.startDate ? new Date(entry.startDate) : undefined}
          onChange={(date) =>
            onChange({ startDate: date?.toISOString() ?? "" })
          }
        />
        <CustomDatePicker
          label={MEDICATION_LABELS.endDate}
          placeholder={MEDICATION_PLACEHOLDERS.date}
          minDate={dayjs(entry.startDate || new Date())}
          value={entry.endDate ? new Date(entry.endDate) : undefined}
          onChange={(date) => onChange({ endDate: date?.toISOString() ?? "" })}
        />
        <TextField
          size="small"
          multiline
          minRows={2}
          maxRows={FREE_TEXT_MAX_ROWS}
          sx={FULL_WIDTH_SX}
          label={inputLabel(MEDICATION_LABELS.note)}
          placeholder={MEDICATION_PLACEHOLDERS.note}
          InputLabelProps={{ shrink: true }}
          value={entry.note}
          onChange={(e) => onChange({ note: e.target.value })}
        />
        <Box
          sx={{ ...FULL_WIDTH_SX, display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            variant="outlined"
            size="small"
            disabled={isActionDisabled}
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        </Box>
      </Box>
    </EntryCard>
  );
};

export default MedicationEntryCard;
